import React from "react"
import {
  Button,
  Checkbox,
  HoverCard,
  LoadingOverlay,
  Text,
  Tooltip,
} from "@mantine/core"
import { useEffect, useState } from "react"
import { Link, useLocation, useParams } from "react-router-dom"
import {
  CollectionQuery,
  Filter,
  Order,
} from "../../../models/collection.model"
import {
  EntityConfig,
  entityViewMode,
} from "../../../models/entity-config.model"
import { Owner } from "../../../models/owner.model"
import EntityList from "../../../shared/component/entity-list/entity-list.component"
import {
  useBulkToggleTenantStatusMutation,
  useLazyCountTenantsQuery,
  useLazyGetTenantsQuery,
} from "../store/tenant.query"
import { Tenant } from "../../../models/tenant.model"
import { IconFileExport, IconFileImport } from "@tabler/icons-react"
import { useDisclosure } from "@mantine/hooks"
import { exportTenants } from "../api/tenant.api"
import ExportModalComponent from "../../../shared/component/export-modal/component/export-modal.component"
import ImportTenantComponent from "../component/import-tenant-component"
import { motion } from "framer-motion"
import StatCard from "../../dashboard/component/stat-cart"
import { AlertTriangle, DollarSign, Package, TrendingUp } from "lucide-react"
import { dateFormat } from "../../../shared/utility/date-format/date-format"
import { useCurrentUser } from "../../../shared/hooks/current-user.hook"
import { useTranslation } from "react-i18next"

export default function TenantPage() {
  const params = useParams()
  const location = useLocation()
  const { t } = useTranslation()
  const [viewMode, setViewMode] = useState<entityViewMode>("list")
  const [openedExport, { open: openExport, close: closeExport }] =
    useDisclosure()
  const [openedImport, { open: openImport, close: closeImport }] =
    useDisclosure()

  const [collection, setCollection] = useState<CollectionQuery>({
    skip: 0,
    top: 10,
    includes: ["owner"],
    orderBy: [{ field: "createdAt", direction: "desc" }],
  })

  const [getTenants, tenants] = useLazyGetTenantsQuery()
  const [countTenants, totalTenants] = useLazyCountTenantsQuery()
  const [countActiveTenants, activeTenants] = useLazyCountTenantsQuery()
  const [countInactiveTenants, inactiveTenants] = useLazyCountTenantsQuery()
  const [bulkToggleStatus, bulkToggleStatusResponse] = useBulkToggleTenantStatusMutation()
  const [userInfo, userId, ownerInfo] = useCurrentUser()
  const generateFilterOption = (value: boolean): Filter[][] => {
    if (userInfo.role.key == "super_admin") {
      return [[{ field: "enabled", value: value as boolean, operator: "=" }]]
    }
    return [
      [{ field: "enabled", value: value as boolean, operator: "=" }],
      [{ field: "ownerId", value: ownerInfo?.parentOwnerId, operator: "=" }],
    ]
  }
  useEffect(() => {
    if (userInfo?.role?.key == "super_admin") {
      getTenants({
        ...collection,
        filter: [
          [{ field: "owner.confidential", value: false, operator: "=" }],
        ],
      })
    } else {
      getTenants({
        ...collection,
        filter: [
          [
            {
              field: "ownerId",
              value: ownerInfo?.parentOwnerId,
              operator: "=",
            },
          ],
        ],
      })
    }
  }, [collection])
  useEffect(() => {
    if (userInfo.role.key == "super_admin") {
      countTenants({
        count: true,
      })
    } else {
      countTenants({
        count: true,
        filter: [
          [
            {
              field: "ownerId",
              value: ownerInfo?.parentOwnerId,
              operator: "=",
            },
          ],
        ],
      })
    }
    countActiveTenants({
      count: true,
      filter: generateFilterOption(true),
    })
    countInactiveTenants({
      count: true,
      filter: generateFilterOption(false),
    })
  }, [])

  useEffect(() => {
    if (params?.id !== undefined) {
      setViewMode("detail")
    } else {
      setViewMode("list")
    }
    if (location.pathname === "/tenant/archived") {
      setViewMode("detail")
    }
  }, [setViewMode, params?.id, location])

  const data: Tenant[] | undefined = tenants.data?.data

  const config: EntityConfig<Tenant> = {
    primaryColumn: {
      key: "name",
      name: t("name"),
      render: (value) => {
        return value.name && value.name.length > 20 ? (
          <>
            <img
              src={value?.gender === "male" ? "/male.svg" : "/female.svg"}
              alt="Product"
              className="size-8 rounded-full"
            />
            <Tooltip label={value?.name} withArrow className="z-50">
              <Text size="sm" className={value?.deletedAt ? "text-danger" : ""}>
                {`${value?.name?.substring(0, 20)} ...`}
              </Text>
            </Tooltip>
          </>
        ) : (
          <>
            {" "}
            <img
              src={value?.gender === "male" ? "/male.svg" : "/female.svg"}
              alt="Product"
              className="size-8 rounded-full"
            />
            {value?.name}
          </>
        )
      },
    },
    detailUrl: "detail",
    rootUrl: "/tenant",
    identity: "id",
    name: "name",
    showDetail: true,
    visibleColumn: [
      {
        key: "name",
        name: t("name"),
        render: (user) => {
          return (
            <Text size="sm" className={user?.deletedAt ? "text-danger" : ""}>
              {user?.name}
            </Text>
          )
        },
      },
      // { key: "email", name: t("email") },
      { key: "phoneNumber", name: t("phoneNumber") },
      { key: "tinNumber", name: t("tinNumber") },
      {
        key: "currentMonthPayment",
        name: t("currentMonthPayment"),
        render(value) {
          return value?.currentMonthPayment
            ? `${value?.currentMonthPayment} ETB`
            : "No payment"
        },
      },
      {
        key: "gender",
        name: t("gender"),
        render: (value: Tenant) => {
          return value?.gender?.toLowerCase() === "male" ? t("male") : t("male")
        },
      },
      {
        key: "archiveReason",
        name: t("archiveReason"),
        hideSort: true,
        render: (tenant: Tenant) => {
          if (tenant?.archiveReason) {
            return (
              <HoverCard width={280} shadow="md">
                <HoverCard.Target>
                  <Text size="sm" className={"cursor-pointer"}>
                    {tenant?.archiveReason && tenant?.archiveReason?.length > 25
                      ? tenant?.archiveReason?.substring(0, 22) + "..."
                      : tenant?.archiveReason}
                  </Text>
                </HoverCard.Target>
                <HoverCard.Dropdown
                  className={
                    "text-justify break-all wrap max-h-60 overflow-auto"
                  }
                >
                  <Text size="sm">{tenant.archiveReason}</Text>
                </HoverCard.Dropdown>
              </HoverCard>
            )
          } else {
            return t("notArchived")
          }
        },
      },
      {
        key: "createdAt",
        name: t("registeredAt"),
        render(value) {
          return dateFormat(value?.createdAt)
        },
      },
      {
        key: "enabled",
        name: t("status"),
        render(value) {
          return value.enabled ? t("active") : t("inactive")
        },
      },
    ],
  }

  return (
    <main className="max-w-7xl mx-auto py-6 px-4 lg:px-8 mt-16">
      <LoadingOverlay visible={tenants.isFetching} />

      <motion.div
        className="hidden md:grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 mb-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
      >
        <StatCard
          name={t("totalTenants")}
          icon={Package}
          value={totalTenants?.data?.count}
          color="#6366F1"
        />
        <StatCard
          name={t("activeTenants")}
          icon={TrendingUp}
          value={activeTenants?.data?.count}
          color="#8B5CF6"
        />
        <StatCard
          name={t("inactiveTenants")}
          icon={AlertTriangle}
          value={inactiveTenants?.data?.count}
          color="#EC4899"
        />
        {/* <StatCard name='Total Revenue' icon={DollarSign} value='12.5%' color='#10B981' /> */}
      </motion.div>
      <EntityList
        viewMode={viewMode}
        title={t("tenant", { count: 2 })}
        showExport={true}
        // header={
        //   <>
        //     <div className="h-14 dark:border-gray-500 dark:bg-gray-500 flex items-center justify-between p-2 border-0 border-b">
        //       <div className="h-full flex items-center space-x-4 font-semibold dark:text-white">
        //         {t("tenant", { count: 2 })}
        //       </div>
        //       {
        //         <div className="h-full  space-x-4 flex items-center">
        //           {viewMode == "list" && (
        //             <>
        //               <Checkbox
        //                 size="sm"
        //                 onChange={(e) =>
        //                   setCollection({
        //                     ...collection,
        //                     withArchived: e.currentTarget.checked
        //                       ? true
        //                       : false,
        //                   })
        //                 }
        //                 label={
        //                   <span className="text-xs text-gray-500 dark:text-gray-400">
        //                     {t("showArchived")}
        //                   </span>
        //                 }
        //               />

        //               <Button
        //                 classNames={{ label: "flex space-x-2 text-white" }}
        //                 className="bg-primary dark:bg-dark_primary"
        //                 size="xs"
        //                 type="button"
        //                 onClick={(e) => openExport()}
        //                 variant="filled"
        //               >
        //                 <span className="text-white">
        //                   <IconFileExport />
        //                 </span>
        //                 <span>{t("export")}</span>
        //               </Button>
        //               <Button
        //                 classNames={{ label: "flex space-x-2 text-white" }}
        //                 className="bg-primary dark:bg-dark_primary"
        //                 size="xs"
        //                 type="button"
        //                 onClick={(e) => openImport()}
        //                 variant="filled"
        //               >
        //                 <span className="text-white">
        //                   <IconFileImport />
        //                 </span>
        //                 <span>{t("import")}</span>
        //               </Button>
        //             </>
        //           )}
        //           <Link to={`/tenant/new`} state={{ id: location?.state?.id }}>
        //             <Button
        //               classNames={{ label: "flex space-x-2 text-white" }}
        //               className="bg-primary dark:bg-dark_primary"
        //               size="xs"
        //               variant="filled"
        //             >
        //               <span className="text-white">
        //                 <svg
        //                   xmlns="http://www.w3.org/2000/svg"
        //                   className="fill-current h-4 text-white"
        //                   viewBox="0 0 24 24"
        //                 >
        //                   <path
        //                     fillRule="evenodd"
        //                     d="M11 2L11 11L2 11L2 13L11 13L11 22L13 22L13 13L22 13L22 11L13 11L13 2Z"
        //                   />
        //                 </svg>
        //               </span>
        //               <span>{t("new")}</span>
        //             </Button>
        //           </Link>
        //         </div>
        //       }
        //     </div>
        //   </>
        // }
        total={tenants?.data?.count}
        itemsLoading={tenants.isFetching}
        config={config}
        items={data}
        initialPage={1}
        defaultPageSize={10}
        pageSize={[10, 15, 20]}
        collectionQuery={collection}
        onArchivedChecked={(e) =>
          setCollection({
            ...collection,
            withArchived: e,
          })
        }
        onPaginationChange={(skip: number, top: number) => {
          if (collection.skip !== skip || collection.top !== top) {
            setCollection({ ...collection, skip: skip, top: top })
          }
        }}
        onSearch={(data: any) => {
          setCollection({
            ...collection,
            search: data,
            searchFrom: ["name", "phoneNumber", "tinNumber"],
          })
        }}
        onFilterChange={(data: any) => {
          if (collection?.filter || data.length > 0) {
            setCollection({ ...collection, filter: data })
          }
        }}
        onOrder={(data: Order) =>
          setCollection({ ...collection, orderBy: [data] })
        }
        onExport={openExport}

        onBulkActivate={(items) => {

          if(items && items.length > 0) bulkToggleStatus({
              ids: items?.map((item) => item.id) as string[],
              enabled: true
            })
            
        }}

        onBulkDeactivate={(items) => {
          if(items && items.length > 0) bulkToggleStatus({
            ids: items?.map((item) => item.id) as string[],
            enabled: false
          })
        }}

        bulkActionLoading={bulkToggleStatusResponse.isLoading}
      />

      <ExportModalComponent
        open={openExport}
        opened={openedExport}
        close={closeExport}
        filename={"tenant.xlsx"}
        filteringVariables={[
          [
            {
              field: "enabled",
              value: true,
              name: t("active"),
              operator: "=",
            },
            {
              field: "enabled",
              value: false,
              name: t("inactive"),
              operator: "=",
            },
          ],
          [
            {
              field: "gender",
              value: "male",
              name: t("male"),
              operator: "=",
            },
            {
              field: "gender",
              value: "female",
              name: t("female"),
              operator: "=",
            },
          ],
        ]}
        exportFunction={exportTenants}
      />

      <ImportTenantComponent
        open={openImport}
        opened={openedImport}
        close={closeImport}
      />
    </main>
  )
}
